// Business rules for the mock adapter — the part of mock/server.js that
// isn't just CRUD. Pulled into its own file so it can be unit-tested
// (mock/specs/rules.spec.js) without going through the request() plumbing.
//
// Scope note: until Milestone 2 ships Layers 1–3 (agent-specific
// assignments, leave, regular schedules), every assigned agent is treated
// as working the property's Layer 0 opening hours — see
// docs/real-estate/be-handover-2026-09-28.md's M2 outline. `db.unavailability`
// /`db.schedules`/`db.specificAssignments` exist in the seed already so
// M2 only has to start reading them here, not restructure the DB.

const ACTIVE_STATUSES = ['requested', 'confirmed', 'rescheduled'];

export function bedroomsLabel(bedrooms, bedroomsExtra) {
  if (bedrooms == null) return null;
  return bedroomsExtra ? `${bedrooms}+${bedroomsExtra}` : String(bedrooms);
}

// Never typed by the client — always derived from the project's floor
// plans, same rule as the old client-side roll() this replaces (FE handoff
// §4.1: "computed rollup — never typed").
// Square feet per unit, for the rollup of floor plans that mix units.
const SQFT_PER_UNIT = {
  sqft: 1,
  sqm: 10.7639,
  sqyd: 9,
  sqin: 1 / 144,
  sqcm: 0.00107639,
};

// Same rule as the BE: one shared unit is kept as is; mixed units are
// converted to sqft (rounded to 2 decimals). Floor plans without a size
// are left out of the range — a missing size is not a zero.
function sizeRollup(units) {
  const sized = units.filter(u => u.built_up_size != null);
  if (!sized.length) return { min: null, max: null, unit: null };
  const unitCodes = [...new Set(sized.map(u => u.built_up_unit || 'sqft'))];
  const shared = unitCodes.length === 1;
  const sizes = sized.map(u =>
    shared
      ? u.built_up_size
      : Math.round(
          u.built_up_size * SQFT_PER_UNIT[u.built_up_unit || 'sqft'] * 100
        ) / 100
  );
  return {
    min: Math.min(...sizes),
    max: Math.max(...sizes),
    unit: shared ? unitCodes[0] : 'sqft',
  };
}

export function computeRollup(units) {
  if (!units.length) {
    return {
      price_min: null,
      price_max: null,
      currency: null,
      mixed_currency: false,
      size_min: null,
      size_max: null,
      size_unit: null,
      max_bedrooms: 0,
      max_bathrooms: 0,
      max_parking_lots: 0,
      floor_plan_count: 0,
    };
  }
  const currencies = [...new Set(units.map(u => u.currency || 'RM'))];
  const size = sizeRollup(units);
  const mixedCurrency = currencies.length > 1;
  return {
    price_min: mixedCurrency ? null : Math.min(...units.map(u => u.price_from)),
    price_max: mixedCurrency ? null : Math.max(...units.map(u => u.price_from)),
    currency: mixedCurrency ? null : currencies[0],
    mixed_currency: mixedCurrency,
    size_min: size.min,
    size_max: size.max,
    size_unit: size.unit,
    max_bedrooms: Math.max(...units.map(u => u.bedrooms || 0)),
    max_bathrooms: Math.max(...units.map(u => u.bathrooms || 0)),
    max_parking_lots: Math.max(...units.map(u => u.parking_lots || 0)),
    floor_plan_count: units.length,
  };
}

const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Groups identical weekly rows across consecutive days into "Mon–Sat
// 10:00–13:00, 14:00–18:00" style text. hours: [{day_of_week, start_time,
// end_time}].
export function openingHoursText(hours) {
  if (!hours.length) return 'Closed';
  const byDay = {};
  hours.forEach(h => {
    byDay[h.day_of_week] = byDay[h.day_of_week] || [];
    byDay[h.day_of_week].push(`${h.start_time}–${h.end_time}`);
  });
  const dayKeys = Object.keys(byDay)
    .map(Number)
    .sort((a, b) => a - b);
  if (!dayKeys.length) return 'Closed';

  const segments = [];
  let runStart = dayKeys[0];
  let runEnd = dayKeys[0];
  let runRows = byDay[dayKeys[0]].join(', ');
  const flush = () => {
    const label =
      runStart === runEnd
        ? DAY_ABBR[runStart]
        : `${DAY_ABBR[runStart]}–${DAY_ABBR[runEnd]}`;
    segments.push(`${label} ${runRows}`);
  };
  for (let i = 1; i < dayKeys.length; i += 1) {
    const day = dayKeys[i];
    const rows = byDay[day].join(', ');
    if (day === runEnd + 1 && rows === runRows) {
      runEnd = day;
    } else {
      flush();
      runStart = day;
      runEnd = day;
      runRows = rows;
    }
  }
  flush();
  return segments.join(', ');
}

// Mon=0..Sun=6, same convention as day_of_week (matches Organization) —
// JS's Date#getDay() is Sun=0..Sat=6, so shift it.
export function apiDayOfWeek(date) {
  return (date.getDay() + 6) % 7;
}

export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function parseTime(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}
function formatTime(minutes) {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const m = (minutes % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

// Every Layer-0 row for `propertyId` on the weekday of `dateStr`, with any
// date override for that exact date applied (an override with both times
// null means closed all day; a same-day operatingHoursOverrides array
// entry can only exist once per property/date in this mock).
export function hoursForDate(db, propertyId, dateStr) {
  const override = (db.operatingHoursOverrides[propertyId] || []).find(
    o => o.override_date === dateStr
  );
  if (override) {
    if (override.closed || override.start_time == null) return [];
    return [{ start_time: override.start_time, end_time: override.end_time }];
  }
  const day = apiDayOfWeek(new Date(`${dateStr}T00:00:00`));
  return (db.operatingHours[propertyId] || [])
    .filter(h => h.day_of_week === day)
    .map(h => ({ start_time: h.start_time, end_time: h.end_time }));
}

function clipToNow(windows, dateStr) {
  if (dateStr !== todayStr()) return windows;
  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
  return windows
    .map(w => ({
      start_time: formatTime(Math.max(parseTime(w.start_time), nowMinutes)),
      end_time: w.end_time,
    }))
    .filter(w => parseTime(w.start_time) < parseTime(w.end_time));
}

function agentBusyIntervals(db, agentId, dateStr, excludeAppointmentId) {
  return db.appointments
    .filter(
      a =>
        a.agent_id === agentId &&
        a.date === dateStr &&
        ACTIVE_STATUSES.includes(a.status) &&
        a.appointment_id !== excludeAppointmentId
    )
    .map(a => ({ start: parseTime(a.start_time), end: parseTime(a.end_time) }));
}

function isAgentFree(
  db,
  agentId,
  dateStr,
  startMin,
  endMin,
  excludeAppointmentId
) {
  return !agentBusyIntervals(db, agentId, dateStr, excludeAppointmentId).some(
    b => startMin < b.end && endMin > b.start
  );
}

// Core availability computation, shared by the Portal and Bot surfaces
// (A12/B6) and by create/reschedule's own re-validation. Returns
// `{ arrival_windows, slots, unavailable_reason }` — exactly one of
// arrival_windows/slots is non-null, matching the API's response shape.
export function computeSlots(
  db,
  {
    propertyId,
    appointmentType, // 'viewing' | 'key_handover'
    durationMinutes, // required for key_handover, ignored (project's own) for viewing
    agentId, // optional: narrow to one agent
    dateStr,
    excludeAppointmentId,
  }
) {
  const property = db.properties.find(p => p.property_id === propertyId);
  const isWalkInViewing =
    appointmentType === 'viewing' && property.booking_mode === 'walk_in';

  if (dateStr < todayStr()) {
    return {
      arrival_windows: null,
      slots: isWalkInViewing ? null : [],
      unavailable_reason: 'past_date',
    };
  }

  const windows = clipToNow(hoursForDate(db, propertyId, dateStr), dateStr);
  if (!windows.length) {
    return {
      arrival_windows: null,
      slots: isWalkInViewing ? null : [],
      unavailable_reason: 'closed_that_day',
    };
  }

  if (isWalkInViewing) {
    return { arrival_windows: windows, slots: null, unavailable_reason: null };
  }

  // Agent-mode viewing or a key handover (handovers are always agent-based,
  // even at a walk-in property — PRD §4.2).
  const duration =
    appointmentType === 'key_handover'
      ? durationMinutes
      : property.viewing_duration_minutes;
  const candidateAgentIds = agentId ? [agentId] : property.agent_ids;

  if (!candidateAgentIds.length) {
    return {
      arrival_windows: null,
      slots: [],
      unavailable_reason: 'no_agent_assigned',
    };
  }
  // "Working that day" == the property is open (Layer 0 fallback, until
  // M2's Layers 1/2/3 narrow this per agent).
  const workingAgentIds = candidateAgentIds;

  const slots = [];
  windows.forEach(w => {
    for (
      let start = parseTime(w.start_time);
      start + duration <= parseTime(w.end_time);
      start += duration
    ) {
      const end = start + duration;
      const freeAgentIds = workingAgentIds.filter(id =>
        isAgentFree(db, id, dateStr, start, end, excludeAppointmentId)
      );
      if (!freeAgentIds.length) continue; // eslint-disable-line no-continue

      let capacityRemaining = null;
      if (appointmentType === 'viewing') {
        const overlappingViewings = db.appointments.filter(
          a =>
            a.property_id === propertyId &&
            a.appointment_type === 'viewing' &&
            a.date === dateStr &&
            ACTIVE_STATUSES.includes(a.status) &&
            a.appointment_id !== excludeAppointmentId &&
            parseTime(a.start_time) < end &&
            parseTime(a.end_time) > start
        ).length;
        capacityRemaining =
          property.slot_capacity != null
            ? Math.min(
                freeAgentIds.length,
                property.slot_capacity - overlappingViewings
              )
            : freeAgentIds.length;
        if (capacityRemaining <= 0) continue; // eslint-disable-line no-continue
      }

      slots.push({
        start_time: formatTime(start),
        end_time: formatTime(end),
        available_agent_ids: freeAgentIds,
        capacity_remaining: capacityRemaining,
      });
    }
  });

  if (!slots.length) {
    return {
      arrival_windows: null,
      slots: [],
      unavailable_reason: 'fully_booked',
    };
  }
  return { arrival_windows: null, slots, unavailable_reason: null };
}

// Picks the free agent with the fewest non-cancelled appointments that day
// across every property (PRD §4.4); ties broken by the order agentIds were
// given in, which is deterministic here (db.properties[].agent_ids order)
// rather than the spec's "pick one at random" — a deterministic mock is
// more useful to develop against than a flaky one.
export function autoAssignAgent(db, agentIds, dateStr) {
  let best = null;
  let bestCount = Infinity;
  agentIds.forEach(id => {
    const count = db.appointments.filter(
      a =>
        a.agent_id === id &&
        a.date === dateStr &&
        ACTIVE_STATUSES.includes(a.status)
    ).length;
    if (count < bestCount) {
      bestCount = count;
      best = id;
    }
  });
  return best;
}

// GET .../availability/dates — walks the range day by day reusing
// computeSlots' per-day reason, since a "date" is just "does this day have
// at least one arrival window / slot".
export function computeDates(db, opts, fromDateStr, toDateStr) {
  const availableDates = [];
  const unavailableDates = [];
  const cursor = new Date(`${fromDateStr}T00:00:00`);
  const end = new Date(`${toDateStr}T00:00:00`);
  while (cursor <= end) {
    const dateStr = cursor.toISOString().slice(0, 10);
    const result = computeSlots(db, { ...opts, dateStr });
    if (result.unavailable_reason) {
      unavailableDates.push({
        date: dateStr,
        reason: result.unavailable_reason,
      });
    } else {
      availableDates.push(dateStr);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return {
    available_dates: availableDates,
    unavailable_dates: unavailableDates,
  };
}

export function hasFutureAppointment(db, matches) {
  const today = todayStr();
  return db.appointments.some(
    a => matches(a) && a.status !== 'cancelled' && a.date >= today
  );
}
